import React, { useEffect, useState } from 'react';
import { cx } from '@linaria/core';
import { either } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { observer } from 'mobx-react-lite';
import { textRegular } from 'styles/typography';
import { StatusCode } from 'api/grpc/resources';
import { useUnsafePredictionStore } from 'modules/Predictions/contexts/MstPredictionStoreCtx';
import { reactLeftToJSX, UI_STATES } from 'utils/uiStates/uiStates';
import { withGuaranteedPredStore } from 'modules/Predictions/withGuaranteedPredStore';
import { IModelPredictionStoreMST } from 'modules/Predictions/mst/ModelPredictionStore';
import { BoxLoader } from 'components/BoxLoader';
import { ModelOutputRenderer } from 'components/ModelOutputRenderer/ModelOutputRenderer';
import { subtitle } from 'modules/PageNotFound/PageNotFound.styles';
import { useIsLoggedIn } from 'modules/Auth/hooks';
import { sidebar, sidebarWrapper } from './Sidebar.styles';
import { RetryErrorUI, FetchingErrorUI, LoginOrSignUpErrorUI } from './ErrorUI';

const MAX_RETRIES = 30;

const SummaryView = observer((): JSX.Element | null => {
  const store = useUnsafePredictionStore<CF.API.Models.Model, IModelPredictionStoreMST>();
  const [retryCount, setRetryCount] = useState(MAX_RETRIES);
  const isLoggedIn = useIsLoggedIn();
  const model = store.entity.data;

  const modelOutputs = pipe(
    store.outputE,
    either.fold(
      // NON-200 RESPONSE(s) ❌
      reactLeftToJSX({
        [UI_STATES.error]: (error) => {
          const errorStatusCode = error?.errorResponse?.status?.code;

          const isUnsupportedInput = errorStatusCode === StatusCode.INPUT_UNSUPPORTED_FORMAT;
          const title = isUnsupportedInput ? undefined : error?.title;

          const description = isUnsupportedInput ? undefined : error?.reason;
          return !isLoggedIn ? <LoginOrSignUpErrorUI /> : <FetchingErrorUI title={title} description={description} />;
        },
        [UI_STATES.connectionError]: (error) => {
          const errorStatusCode = error?.errorResponse?.status?.code;
          if (
            (errorStatusCode === StatusCode.MODEL_DEPLOYING ||
              errorStatusCode === StatusCode.MODEL_BUSY_PLEASE_RETRY ||
              errorStatusCode === StatusCode.MODEL_LOADING) &&
            store.media.currentInput &&
            retryCount > 0
          ) {
            store.predict(store.media.currentInput.data);
            setRetryCount(retryCount - 1);
          }
          return (
            <div className={sidebarWrapper}>
              <RetryErrorUI />
            </div>
          );
        },
        [UI_STATES.loading]: () => {
          const isRetrying = retryCount > 0 && retryCount < MAX_RETRIES;

          return (
            <div className={sidebarWrapper}>
              <BoxLoader
                data-testid="spinner-ui"
                className="loader"
                type="spinner"
                title={`Model ${isRetrying ? 'deployment in progress...' : 'inference in progress...'}`}
                content="Loading Model for the first time may take a few minutes."
              />

              {isRetrying && (
                <h2 className={cx(textRegular, subtitle)}>
                  This may take a few minutes, once deployed it will be ready for use. Reach out if you&rsquo;re interested in dedicated compute
                  without any start-up time.
                </h2>
              )}

              {retryCount === 0 && <h2 className={cx(textRegular, subtitle)}>Request timed out. Please try again in some time.</h2>}
            </div>
          );
        },
      }),
      // GOOD RESPONSE ✅
      (output) => {
        return (
          <>
            {output.map((modelOutput) => {
              const regionColorMap: Record<string, string> | undefined = pipe(
                store.regionListE,
                either.match(
                  () => undefined,
                  (outputData) => {
                    const colorMap: Record<string, string> = {};
                    outputData.items.forEach((item) => {
                      colorMap[item.key] = item.regionData.color;
                    });
                    return colorMap;
                  },
                ),
              );

              const collapsibleBarProps = {
                minThreshold: store.viewState.minThreshold || 0,
                setMinThreshold: (num: number) => store.viewState.setMinThreshold(num),
                isModelVersionsDropdownVisible: true,
              };

              return (
                <ModelOutputRenderer
                  key={modelOutput.id}
                  model={model}
                  modelOutput={modelOutput}
                  isOutputSuppressed={false}
                  regionColorMap={regionColorMap || {}}
                  collapsibleBarProps={collapsibleBarProps}
                />
              );
            })}
          </>
        );
      },
    ),
  );

  useEffect(() => {
    store.setLastEligibleNode();
    const versionStatus = store.entity.data.model_version.status.code;
    store.setHasTrained(versionStatus === StatusCode.MODEL_TRAINED);
  }, [store.entity]);

  return <>{modelOutputs}</>;
});

function Sidebar_(): React.ReactElement | null {
  return (
    // id needed to control the sidebar styles from the playground container
    <div id="prediction-viewcore-sidebar" className={sidebar}>
      <SummaryView />
    </div>
  );
}

export const Sidebar = withGuaranteedPredStore(observer(Sidebar_));
